import { Button, Checkbox, Form, Radio, Select, Space } from "antd";
import TextArea from "antd/es/input/TextArea";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import AddIcon from "../../../assets/icons/AddIcon";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import SelectDropdownLgIcon from "../../../assets/icons/SelectDropdownLgIcon";
import IMG from "../../../assets/images/avatar.svg";
import { useGetDepartmentsQuery } from "../../../features/department/departmentApi";
import { useGetEmployeesQuery } from "../../../features/employee/employeeApi";
import {
  useAddApprovalSettingMutation,
  useUpdateApprovalSettingMutation,
} from "../../../features/leaveApproval/leaveApprovalApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import { toast } from "../../../lib/toast";
import { ApprovalData } from "../../../pages/admin/Settings/Approval/Approval";
import { ModalComponentProps } from "../../../types";
import { capitalize } from "../../../utils/string";
import { addKeyInArray, selectSearchOption } from "../../../utils/utils";
import SectionHeader from "../../common/SectionHeader";
import CustomModal from "../../ui/CustomModal";
import CustomTable from "../../ui/CustomTable";
import Loader from "../../ui/Loader";
import ApproversModal from "./ApproversModal";

export type SelectDataType = {
  id: number | string;
  name?: string;
  department?: string;
  employeeId?: string | null;
  img?: string;
  systemId?: string;
};

export default function AddNewApprovalModal({
  handleClose,
  type,
  data,
  ...rest
}: ModalComponentProps<ApprovalData>) {
  const [modules, setModules] = useState("leave");
  const [target_type, setTarget_type] = useState<
    "department" | "employee" | "global"
  >("global");
  // const [target_id, setTarget_id] = useState<number | undefined>();
  const [remarks, setRemarks] = useState<undefined | string>();
  const [approvers, setApprovers] = useState<SelectDataType[]>([]);
  const [departmentId, setDepartmentId] = useState<number | undefined>(
    undefined
  );
  const [employeeId, setEmployeeId] = useState<number | undefined>(undefined);
  // const {
  //   open,
  //   handleOpen,
  //   handleClose: handleModalClose,
  // } = useModal<"department" | "employee", "TYPE">();
  const {
    open: approverOpen,
    handleOpen: handleApproverOpen,
    handleClose: handleApproverClose,
  } = useModal<SelectDataType>();
  const {
    isLoading: isDepartmentLoading,
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const [addApprovalSetting, { isError, error, isLoading, isSuccess }] =
    useAddApprovalSettingMutation();
  const [
    updateApprovalSetting,
    {
      isError: isUpdateError,
      error: updateError,
      isLoading: isUpdateLoading,
      isSuccess: isUpdateSuccess,
    },
  ] = useUpdateApprovalSettingMutation();
  const {
    isError: isEmployeeError,
    error: employeeError,
    data: employeeData,
  } = useGetEmployeesQuery({
    params: {
      skip: 0,
      limit: 0,
      status: "active",
    },
  });
  useError(isError, error, "Add approval fail");
  useError(isUpdateError, updateError, "Update approval fail");
  useError(isDepartmentError, departmentError, "Get department data fail");
  useError(isEmployeeError, employeeError, "Get employee data fail");

  const handleApprovers = (val: SelectDataType) => {
    if (approverOpen?.type === "ADD") {
      setApprovers((prev) => [...prev, val]);
      handleApproverClose();
    }

    if (approverOpen?.type === "UPDATE") {
      setApprovers((prev) =>
        prev?.map((item) => {
          if (item?.systemId === val?.systemId) {
            return val;
          }

          return item;
        })
      );
      handleApproverClose();
    }
  };

  const handleDelete = (id: string) => {
    setApprovers((prev) => prev.filter((item) => item.systemId !== id));
  };

  const handleSubmit = () => {
    if (target_type === "employee" && !employeeId) {
      toast("error", "Please select an employee");
      return;
    }

    if (target_type === "department" && !departmentId) {
      toast("error", "Please select a department");
      return;
    }

    if (!approvers?.length) {
      toast("error", "Please select at least one approver");
      return;
    }

    const newValues = {
      modules,
      target_type,
      target_id:
        target_type === "employee"
          ? employeeId
          : target_type === "department"
          ? departmentId
          : null,
      note: remarks,
      approvers: approvers?.map((item, idx) =>
        item?.id === "null"
          ? {
              type: "lm",
              type_id: null,
              order: idx + 1,
            }
          : {
              type: "employee",
              type_id: item?.id,
              order: idx + 1,
            }
      ),
    };

    if (type === "UPDATE" && data?.id) {
      updateApprovalSetting({
        id: data?.id,
        body: newValues,
      });
    } else {
      addApprovalSetting(newValues);
    }
  };

  const columns: ColumnsType<SelectDataType> = [
    {
      title: <span className="text-nowrap">Approver #</span>,
      render: (_, __, idx) => `#${idx + 1}`,
    },
    {
      title: "Employee",
      render: (val: SelectDataType) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10">
            <img
              className="w-full h-full rounded-full"
              src={val?.img || IMG}
              alt=""
            />
          </div>
          <div className="flex-1 flex flex-col gap-[2px] text-sm font-medium text-display-body">
            {val?.name && (
              <h6 className="text-body text-base font-semibold">{val?.name}</h6>
            )}
            {(val?.employeeId || val?.department) && (
              <p className="text-extra-gray">
                ID: {val?.employeeId || "n/a"}{" "}
                {val?.department ? <span>, {val?.department}</span> : null}
              </p>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Action",
      // dataIndex: "systemId",
      render: (val) => (
        <Space size={8}>
          <Button
            icon={<EditIcon />}
            size="small"
            className="action-button"
            onClick={() =>
              handleApproverOpen({
                type: "UPDATE",
                data: val,
              })
            }
          />
          <Button
            icon={<DeleteIcon />}
            size="small"
            className="action-button"
            onClick={() => handleDelete(val?.systemId)}
          />
        </Space>
      ),
    },
  ];

  useEffect(() => {
    if (type === "UPDATE") {
      if (data?.target_type) setTarget_type(data?.target_type);

      if (data?.target_type === "department" && data?.target_id) {
        setDepartmentId(data?.target_id);
      }

      if (data?.target_type === "employee" && data?.target_id) {
        setEmployeeId(data?.target_id);
      }

      if (data?.approvers?.length) {
        setApprovers(
          data?.approvers?.map((item) => {
            if (item?.type === "lm") {
              return {
                id: "null",
                name: "Line Manager (LM)",
                systemId: item?.id.toString(),
              };
            }

            return {
              id: item?.approver_details?.business_member_id,
              name: item?.approver_details?.name,
              department: item?.approver_details?.department_name,
              employeeId: item?.approver_details?.employee_id,
              img: item?.approver_details?.profile_image,
              systemId: item?.id.toString(),
            };
          })
        );
      }

      setRemarks(data?.note);
    }
  }, [data?.approvers, data?.note, data?.target_id, data?.target_type, type]);

  // success toast message
  useEffect(() => {
    if (isSuccess) {
      toast("success", "Approval added successfully");
      return handleClose();
    }
  }, [handleClose, isSuccess]);
  useEffect(() => {
    if (isUpdateSuccess) {
      toast("success", "Approval update successfully");
      return handleClose();
    }
  }, [handleClose, isUpdateSuccess]);

  return (
    <CustomModal
      title={type === "UPDATE" ? "Update Approval" : "Add New Approval"}
      {...rest}
      handleClose={handleClose}
      width={600}
      footer={null}
    >
      {/* {open.type === "TYPE" && (
        <TypeModal
          open={open.open}
          data={open?.data}
          handleType={handleType}
          handleClose={handleModalClose}
        />
      )} */}
      {(approverOpen.type === "ADD" || approverOpen?.type === "UPDATE") && (
        <ApproversModal
          open={approverOpen.open}
          data={approverOpen?.data}
          type={approverOpen?.type}
          approvers={approvers}
          handleApprovers={handleApprovers}
          handleClose={handleApproverClose}
        />
      )}
      {/* Approval Options */}
      <div className="custom-form mb-4">
        <SectionHeader title="Approval Options" />

        <ul className="mb-2">
          <li className="py-2 flex items-center justify-between">
            <h6 className="min-w-[130px] text-header text-sm font-medium">
              Choose Module:
            </h6>
            <div className="flex-1 flex items-center gap-6">
              <Radio.Group
                value={modules}
                onChange={(e) => setModules(e.target.value)}
                className="custom-radio text-header text-sm font-medium"
              >
                {approverModuleList?.map((val) => (
                  <Radio value={val} key={val}>
                    {capitalize(val)}
                  </Radio>
                ))}
              </Radio.Group>
            </div>
          </li>
          <li className="py-2 flex items-center justify-between">
            <h6 className="min-w-[130px] text-header text-sm font-medium">
              Choose Type:
            </h6>
            <div className="flex-1 flex items-center gap-6">
              <Radio.Group
                value={target_type}
                onChange={(e) =>
                  // e.target.value === "global"
                  //   ? setTarget_type(e.target.value)
                  //   : handleOpen({
                  //       type: "TYPE",
                  //       data: e.target.value,
                  //     })
                  setTarget_type(e.target.value)
                }
                className="custom-radio text-header text-sm font-medium"
              >
                {typeList?.map((val) => (
                  <Radio value={val} key={val}>
                    {capitalize(val)}
                  </Radio>
                ))}
              </Radio.Group>
            </div>
          </li>
        </ul>
        {target_type === "department" ? (
          <div className="mb-3">
            {isDepartmentLoading ? (
              <Loader />
            ) : (
              <div className="grid grid-cols-4 gap-x-1 gap-y-2 p-2.5 border-2 border-light border-dashed">
                {departmentData?.data?.departments?.map((item) => (
                  <Checkbox
                    key={item?.id}
                    className="text-header !text-sm font-medium"
                    checked={departmentId === item?.id}
                    onChange={(e) => {
                      if (e.target.checked) setDepartmentId(item?.id);
                    }}
                  >
                    {item?.name}
                  </Checkbox>
                ))}
              </div>
            )}
          </div>
        ) : target_type === "employee" ? (
          <Form
            className="custom-form"
            layout="vertical"
            requiredMark="optional"
            size="large"
          >
            <Form.Item label="Select Employee " className="mb-4">
              <Select
                placeholder="Select"
                size="large"
                allowClear
                showSearch
                filterOption={selectSearchOption}
                options={employeeData?.data?.employees?.map((item) => ({
                  label: item?.member?.profile?.name,
                  value: item?.id,
                }))}
                suffixIcon={<SelectDropdownLgIcon />}
                value={employeeId}
                onChange={(val) => setEmployeeId(val)}
              />
            </Form.Item>
          </Form>
        ) : null}
        <div>
          <h6 className="text-body text-sm font-medium mb-2">
            {target_type === "global" ? "Remarks:" : "Note:"}
          </h6>
          <TextArea
            rows={3}
            placeholder="Type..."
            className="resize-none"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </div>
      </div>

      {/* Approvers Section */}
      <div>
        <SectionHeader
          title="Approvers"
          buttonText="Add"
          icon={<AddIcon />}
          onButtonClick={() =>
            handleApproverOpen({
              type: "ADD",
            })
          }
        />

        <CustomTable
          loading={false}
          columns={columns}
          dataSource={addKeyInArray(
            approvers?.map((item) => ({
              ...item,
              id: item?.id || Math.random(),
            }))
          )}
          rootClassName="!border-x-0"
        />
      </div>

      <Space className="w-full pt-6 sticky bottom-0 bg-white" size={12}>
        <Button
          className="normal-dark-button modal-button"
          onClick={handleClose}
          size="middle"
        >
          Cancel
        </Button>
        <Button
          className="modal-button"
          type="primary"
          size="middle"
          onClick={handleSubmit}
          loading={isLoading || isUpdateLoading}
        >
          {type === "UPDATE" ? "Update" : "Submit"}
        </Button>
      </Space>
    </CustomModal>
  );
}

export const approverModuleList = [
  "leave",
  "expense",
  // "appraisal",
  "petty_cash",
]; //, "expense", "support"
const typeList = ["global", "department", "employee"];
